import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import './ItamiBody.css';

const preBookingBullets = [
    '外傷・発熱・しびれ悪化・力が入らない等はまず医療機関へ',
    '本施術は医療行為ではありません（効果には個人差があります）',
    '使い捨て鍼を使用し、消毒など衛生管理を徹底しています'
];

const faqItems = [
    {
        question: '痛みはありますか？',
        answer: '心地よい刺激が基本。極細鍼を使用し、衛生管理も徹底しています。'
    },
    {
        question: 'どのくらい通いますか？',
        answer: '週1〜2回×3〜6週が目安。反応を見ながら間隔を調整します。'
    },
    {
        question: '服装は？',
        answer: '動きやすく、関節が出しやすい服装でお越しください。'
    }
];

const painAreas = [
    {
        id: 'neck',
        label: '首',
        title: '首（頸部）',
        summary: '前傾姿勢や長時間のスマホ・PC作業で首周囲の筋肉が緊張し、血行不良が続くと痛みや張りが慢性化しやすくなります。',
        image: '/itami/neck.jpg',
        imageAlt: '首のケアと全身バランス調整を行う様子',
        policy:
            '首の痛みが出ている部位だけでなく、姿勢と全身バランスを整えて筋・筋膜の緊張と頸椎の可動域を回復させる方針です。',
        flowSteps: [
            {
                title: 'Step1 徹底検査と問診',
                text: '首の痛みを生む筋・筋膜や頸椎周囲の緊張ポイントを問診・触診で丁寧に特定します。'
            },
            {
                title: 'Step2 全身バランス調整',
                text: '首だけでなく全身バランスを整える施術で筋・筋膜の柔軟性と頸椎の可動域を取り戻します。'
            },
            {
                title: 'Step3 再発防止サポート',
                text: '画面位置や枕の高さ、セルフストレッチなど生活習慣を見直しながら再発予防をサポートします。'
            }
        ],
        description: [
            '首の痛みの多くは前傾姿勢や長時間のスマホ・PC作業による筋緊張と血行不良が主因です。睡眠不足や枕の高さなど生活要因も痛みを助長します。',
            '頭が前に出るスマホ首（ストレートネック）が続くと頸椎のカーブが崩れ、頭痛や吐きけにつながることがあります。画面位置調整やこまめな休憩が欠かせません。',
            '寝違えや頸椎捻挫、ストレートネック、頸椎ヘルニアなども首の痛みを招き、場合によって手指のしびれや筋力低下を伴うため早期対応が大切です。',
            '放置すると慢性化して可動域が狭くなる、頭痛・吐きけが増えるなど日常動作に支障が出やすくなります。原因へのアプローチと生活環境の見直しが重要です。'
        ],
        approachPoints: [
            '首の痛みを生む筋・筋膜と頸椎の緊張ポイントを徹底した検査と問診で見極めます。',
            '全身の筋肉・筋膜・骨盤バランスを整えて血行と神経の通りを改善します。',
            '作業姿勢・画面位置・枕の高さを見直し、セルフストレッチも含めて再発を防ぎます。'
        ],
        selfCare: [
            'スマホやPCの画面位置を目線の高さに合わせ、前傾姿勢が続かないようこまめに整える。',
            '1時間に一度は立ち上がって肩甲骨や首を軽く動かし、血行不良を防ぐ。',
            '枕の高さや寝姿勢を点検し、首に負担がかかる場合は早めに相談する。'
        ],
        ctaLabel: '首の症状例を見る',
        ctaPath: '/symptoms/neck-pain',
        position: {
            top: '19%',
            left: '52%',
            width: '18%',
            height: '16%'
        }
    },
    {
        id: 'shoulder',
        label: '肩',
        title: '肩（肩コリ・五十肩）',
        summary: '肩コリ・五十肩は筋肉の血行不良や炎症が関わるため、肩だけでなく全身バランスを整えて肩の動きを取り戻します。',
        image: '/itami/shoulder.jpg',
        imageAlt: '肩まわりの施術を行うシーン',
        policy:
            '当院の肩コリ・五十肩施術は、痛みが生じている箇所のみにアプローチするのではなく、肩甲骨や骨盤まで含めた全身の筋肉・筋膜バランスを評価しながら施術を行います。硬くなった筋肉と筋膜を検査と問診で見極め、痛みを誘発しない範囲で反応を整えます。',
        flowSteps: [
            {
                title: 'Step1 機能評価',
                text: '肩コリ・五十肩の原因となっている筋肉や筋膜の硬さを徹底的な検査と問診で究明します。'
            },
            {
                title: 'Step2 全身バランス調整',
                text: '硬くなった筋肉・筋膜に独自の全身調整でアプローチし、筋肉と骨盤のバランスを整えます。'
            },
            {
                title: 'Step3 再発予防トレーニング',
                text: '再発防止のため体の使い方を一緒に見直し、肩を痛めない動きを身につけます。'
            }
        ],
        description: [
            '「肩コリ」と「五十肩」は同じ肩周辺に起こる症状ですが、肩コリの多くは筋肉の血行不良が原因で、姿勢が崩れた長時間のデスクワークや精神的な緊張、手や腕の疲労で筋肉が硬くなります。',
            '五十肩は肩の関節や関節周囲の靭帯・筋・関節包が炎症や拘縮を起こすことで発生し、腕が上がらない、夜に疼いて眠れないといった症状へつながります。',
            '肩や背中の筋肉を動かさない生活や、腕を上げる作業を繰り返す仕事、ゴルフやテニスのスイングなどが症状の引き金になることもあります。',
            '軽い肩コリだからと放っておくと、肩が完全に上がらなくなったり、夜間痛や握力低下で日常生活に支障が出る恐れがあります。',
            '早めにケアを始め、肩を動かせる環境を整えていくことが大切です。'
        ],
        approachPoints: [
            '痛みが出ている箇所だけでなく、肩甲骨や骨盤まで含めた全身バランスを評価します。',
            '硬くなった筋肉・筋膜に手技と鍼でアプローチし、筋肉と骨盤のバランスを整えます。',
            '日常生活で肩を痛めない体の使い方とセルフケアを一緒に確認し、再発を防ぎます。'
        ],
        selfCare: [
            '姿勢を整えながらこまめに肩や背中を動かし、筋肉の血行を保つ。',
            '腕を上げる作業やスポーツの後は肩周囲を軽くほぐし、痛みが強いときは無理をしない。',
            '症状が軽いうちに生活習慣を見直し、違和感を感じたら早めに相談する。'
        ],
        ctaLabel: '肩の症状例を見る',
        ctaPath: '/symptoms/shoulder-pain',
        position: {
            top: '32%',
            left: '52%',
            width: '28%',
            height: '18%'
        }
    },
    {
        id: 'waist',
        label: '腰',
        title: '腰痛',
        summary: '原因が分からない腰痛にも筋肉の緊張が隠れているため、全身バランスを整えて根本からケアします。',
        image: '/itami/waist.jpg',
        imageAlt: '腰の施術で神経整体を体験している様子',
        policy:
            '腰痛の多くは筋肉と筋膜の緊張が引き起こしています。当院では問診と検査で硬くなった部位を究明し、全身のバランスを整えながら血行と神経伝達を回復させていきます。',
        flowSteps: [
            {
                title: 'Step1 徹底検査と問診',
                text: '腰痛の原因となっている筋肉・筋膜が硬くなっている箇所を徹底的な検査と問診で究明します。'
            },
            {
                title: 'Step2 全身バランス調整',
                text: '硬くなった筋肉・筋膜に対して全身の筋肉・筋膜・骨盤バランスを整える施術で反応を整えます。'
            },
            {
                title: 'Step3 再発防止の使い方指導',
                text: '再発を防ぐため体の使い方と日常動作を一緒に見直します。'
            }
        ],
        description: [
            '腰痛に苦しむ多くの方が原因を把握できず、病院でも湿布と痛み止めで終わってしまいますが、痛みは確かに存在します。では身体の内部では何が起こっているのでしょうか。',
            '病気や外傷を除いた腰痛の大半は筋肉の緊張です。硬くなった腰の筋肉が血管を圧迫し、酸素や栄養が届きにくくなります。',
            '筋肉の緊張は神経も圧迫し、痛みの信号が正しく伝わらず慢性化を招きます。椎間板ヘルニア後も痛みが続く方がいるのはこのためです。',
            '放っておくと脊柱管狭窄症や歩行時の強い痛みなどに進む恐れもあり、「そのうち良くなる」と我慢するのは危険です。'
        ],
        approachPoints: [
            '問診と徹底した検査で硬くなった筋肉・筋膜を究明します。',
            '全身の筋肉・筋膜・骨盤バランスを整えて血行と神経伝達を回復させます。',
            '再発防止のため体の使い方を一緒に見直し、安心して動ける方向を育てます。'
        ],
        selfCare: [
            '湿布や痛み止めだけに頼らず、痛みの原因を早めに見直す。',
            '同じ姿勢を続けず、無理のない範囲で腰を動かして血行を保つ。',
            '長時間の歩行で痛みが強まるときは無理をせず、専門家に相談する。'
        ],
        ctaLabel: '腰の症状例を見る',
        ctaPath: '/symptoms/low-back-pain',
        position: {
            top: '52%',
            left: '52%',
            width: '24%',
            height: '20%'
        }
    },
    {
        id: 'knee',
        label: '膝',
        title: '膝（変形性膝関節症・膝蓋大腿痛）',
        summary: '立ち上がり・階段・正座で膝に違和感が出る方へ。生活動作の負担や使い過ぎを見直し、再発しにくい歩き方へ整えます。',
        image: '/itami/knee.jpg',
        imageAlt: '膝の状態についてカウンセリングを受ける患者',
        policy:
            '膝痛は局所だけでなく姿勢・歩き方・筋力低下など複合要因が関わります。当院では問診と徒手検査で負担動作を究明し、膝周囲から骨盤〜足部までアライメントを整えながら再発しにくい使い方を指導します。',
        flowSteps: [
            {
                title: 'Step1 問診と徒手検査',
                text: '膝の痛みが出るタイミングや生活習慣、姿勢・歩行フォームを確認し、痛みを誘発する部位とクセを特定します。'
            },
            {
                title: 'Step2 アライメント調整',
                text: '膝周囲と股関節・足首の筋・筋膜バランスを整え、必要に応じて神経整体で荷重ラインと神経の伝達を調整します。'
            },
            {
                title: 'Step3 自宅ケアと再発予防',
                text: '椅子立ちや段差動作で大腿四頭筋と殿筋を活性化し、靴や姿勢の見直し、自宅ケアまで段階的にサポートします。'
            }
        ],
        description: [
            '膝が痛い・違和感があるのに「そのうち治る」と放置すると、階段や立ち上がりで痛みが強まり日常生活に支障が出やすくなります。',
            '運動不足や座りがちな生活、体重増加、加齢による変化、スポーツや立ち仕事での使い過ぎ、不適切な姿勢や歩き方が膝へ過剰なストレスを与えます。',
            '代表的な疾患には変形性膝関節症、半月板損傷、タナ障害、鵞足炎、靱帯損傷、ジャンパー膝やランナー膝などがあり、曲げ伸ばし痛や腫れ、引っかかり感を伴います。',
            '腫れや熱感が強い、引っかかりが急に生じる、膝が崩れるような不安定感が続く場合は医療機関での検査が必要です。早めの評価とケアで悪化を防ぎます。'
        ],
        approachPoints: [
            '問診と徒手検査で痛みの部位・誘因・姿勢や歩行習慣を丁寧に把握します。',
            '膝周囲から骨盤・足部までの筋・筋膜と神経のバランスを整え、荷重ラインを調整します。',
            '靴や歩幅、自宅ケアの見直しを段階的に指導し、再発しにくい動作を身につけます。'
        ],
        selfCare: [
            '同じ姿勢が続くときはこまめに立ち上がり、軽いストレッチや短時間の歩行で関節を動かす。',
            'クッション性と安定感のある靴を選び、歩幅を少し広げて痛みのない範囲で歩行量を調整する。',
            '痛みが強い日は無理な運動や階段の反復を避け、冷やし過ぎや強いマッサージは控えて早めに相談する。'
        ],
        ctaLabel: '膝の症状例を見る',
        ctaPath: '/symptoms/knee-pain',
        position: {
            top: '74%',
            left: '52%',
            width: '22%',
            height: '18%'
        }
    }
];

const easeOutQuint = (t) => 1 - Math.pow(1 - t, 5);
const easeInQuint = (t) => t * t * t * t * t;

const ItamiBody = () => {
    const [selectedAreaId, setSelectedAreaId] = useState(null);
    const [isSheetVisible, setIsSheetVisible] = useState(false);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const backdropRef = useRef(null);
    const sheetRef = useRef(null);
    const animationFrameRef = useRef(null);
    const animationStartRef = useRef(null);
    const animationDurationOpen = 780;
    const animationDurationClose = animationDurationOpen / 2;

    const selectedArea = selectedAreaId
        ? painAreas.find((area) => area.id === selectedAreaId) ?? null
        : null;

    const animateSheet = useCallback(
        (opening) => {
            const duration = opening ? animationDurationOpen : animationDurationClose;
            cancelAnimationFrame(animationFrameRef.current ?? 0);
            animationStartRef.current = performance.now();

            const tick = (now) => {
                const elapsed = now - (animationStartRef.current ?? now);
                const progress = Math.min(elapsed / duration, 1);
            const easing = opening ? easeOutQuint(progress) : easeInQuint(progress);
            const translateY = opening ? 1 - easing : easing;
                const opacity = opening ? easing : 1 - easing;

                if (sheetRef.current) {
                    sheetRef.current.style.transform = `translateY(${translateY * 100}%)`;
                }
                if (backdropRef.current) {
                    backdropRef.current.style.opacity = `${opacity * 0.85}`;
                }

                if (progress < 1) {
                    animationFrameRef.current = requestAnimationFrame(tick);
                } else {
                    if (sheetRef.current) {
                        sheetRef.current.style.transform = opening ? 'translateY(0)' : 'translateY(100%)';
                    }
                    if (backdropRef.current) {
                        backdropRef.current.style.opacity = opening ? '0.85' : '0';
                    }
                }
            };

            animationFrameRef.current = requestAnimationFrame(tick);
        },
        [animationDurationOpen, animationDurationClose]
    );

    useEffect(() => {
        return () => {
            cancelAnimationFrame(animationFrameRef.current ?? 0);
        };
    }, []);

    // const handleOpen = (areaId) => {
    //     setSelectedAreaId(areaId);
    //     setIsSheetVisible(true);
    //     setIsDetailsOpen(false);
    //     requestAnimationFrame(() => animateSheet(true));
    // };

    const closeSheet = useCallback(() => {
        animateSheet(false);
        setIsSheetVisible(false);
    }, [animateSheet]);

    useEffect(() => {
        if (!isSheetVisible) {
            const timeout = setTimeout(() => {
                setSelectedAreaId(null);
            }, animationDurationClose + 100);
            return () => clearTimeout(timeout);
        }
        return undefined;
    }, [isSheetVisible, animationDurationClose]);

    useEffect(() => {
        if (isSheetVisible) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
        return undefined;
    }, [isSheetVisible]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && isSheetVisible) {
                event.preventDefault();
                closeSheet();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [closeSheet, isSheetVisible]);

    const handleBackdropClick = (event) => {
        if (event.target === backdropRef.current) {
            closeSheet();
        }
    };

    return (
        <section className="itami-body" aria-labelledby="itami-body__title">
            <div className="itami-body__inner">
                <header className="itami-body__header">
                    <h2 id="itami-body__title">
                        どこが痛い？<br /><strong>タップで30秒診断</strong>
                    </h2>
                    
                </header>

                <div className="itami-body__layout">
                    <div
                        className="itami-body__visual"
                        role="presentation"
                        aria-label="人の身体図（クリックで部位の詳細を見る）"
                    >
                        <img
                            src="/itami/body.jpg"
                            alt="首・肩・腰・膝の痛みポイントが示された人体図"
                            className="itami-body__image"
                        />
                        {painAreas.map((area) => (
                            <Link
                                key={area.id}
                                type="button"
                                className="itami-body__hotspot"
                                style={{
                                    top: area.position.top,
                                    left: area.position.left,
                                    width: area.position.width,
                                    height: area.position.height
                                }}
                                // onClick={() => handleOpen(area.id)}
                                to={`${area.ctaPath}`}
                                aria-haspopup="dialog"
                                aria-controls="itami-body-sheet"
                                aria-label={`${area.title}のケア内容を表示`}
                            >
                                <span>{area.label}</span>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>

            <div
                id="itami-body-sheet"
                className={`itami-sheet ${isSheetVisible ? 'itami-sheet--open' : ''} ${selectedArea ? 'itami-sheet--mounted' : ''}`}
                role="dialog"
                aria-modal="true"
                aria-hidden={!selectedArea}
            >
                <div
                    className="itami-sheet__backdrop"
                    ref={backdropRef}
                    onClick={handleBackdropClick}
                />
                <div className="itami-sheet__spacer" onClick={closeSheet} />
                <div className="itami-sheet__container" ref={sheetRef}>
                    {selectedArea && (
                        <div className="itami-sheet__content">
                            <header className="itami-sheet__header">
                                <div className="itami-sheet__hero">
                                    <img src={selectedArea.image} alt={selectedArea.imageAlt} className="itami-sheet__hero-image" />
                                    <div className="itami-sheet__overlay">
                                        <h2 className="itami-sheet__title">{selectedArea.title}</h2>
                                        <p className="itami-sheet__subtitle">当院での評価と施術の流れ、セルフケアをご紹介します。</p>
                                    </div>
                                </div>
                            </header>

                            <div className="itami-sheet__body" aria-describedby="itami-sheet-description">
                                <section className="itami-sheet__section">
                                    <h3><InfoOutlinedIcon className="itami-heading__icon" />1分でわかる施術の考え方</h3>
                                    <p>{selectedArea.policy}</p>
                                </section>

                                <section className="itami-sheet__section">
                                    <h3><TuneOutlinedIcon className="itami-heading__icon" />この部位へのアプローチ</h3>
                                    <p>{selectedArea.summary}</p>

                                    <ol className="itami-sheet__steps">
                                        {selectedArea.flowSteps.map((step) => (
                                            <li key={step.title} className="itami-sheet__step-card">
                                                <strong>{step.title}</strong>
                                                <div>{step.text}</div>
                                            </li>
                                        ))}
                                    </ol>

                                    <div className="itami-sheet__more">
                                        <button
                                            type="button"
                                            className="itami-sheet__more-button"
                                            onClick={() => setIsDetailsOpen((v) => !v)}
                                            aria-expanded={isDetailsOpen}
                                            aria-controls={`${selectedArea.id}-details`}
                                        >
                                            {isDetailsOpen ? '閉じる' : '続きを読む'}
                                        </button>
                                    </div>

                                    {isDetailsOpen && (
                                        <div id={`${selectedArea.id}-details`}>
                                            {selectedArea.description.map((paragraph, index) => (
                                                <p key={`${selectedArea.id}-desc-${index}`}>{paragraph}</p>
                                            ))}
                                            {selectedArea.approachPoints && (
                                                <ul className="itami-sheet__list">
                                                    {selectedArea.approachPoints.map((item) => (
                                                        <li key={`${selectedArea.id}-approach-${item}`}>{item}</li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    )}
                                </section>

                                <section className="itami-sheet__section">
                                    <h3><SelfImprovementIcon className="itami-heading__icon" />ご自宅で試せるセルフケア</h3>
                                    <ul className="itami-sheet__list">
                                        {selectedArea.selfCare.map((item) => (
                                            <li key={`${selectedArea.id}-selfcare-${item}`}>{item}</li>
                                        ))}
                                    </ul>
                                </section>

                                <section className="itami-sheet__section">
                                    <h3><HelpOutlineIcon className="itami-heading__icon" />よくあるご質問</h3>
                                    <dl className="itami-sheet__faq">
                                        {faqItems.map((faq) => (
                                            <Fragment key={faq.question}>
                                                <dt className="itami-sheet__faq-question">{faq.question}</dt>
                                                <dd className="itami-sheet__faq-answer">{faq.answer}</dd>
                                            </Fragment>
                                        ))}
                                    </dl>
                                </section>

                                <section className="itami-sheet__section itami-sheet__section--notice">
                                    <h3><WarningAmberIcon className="itami-heading__icon" />ご予約前にご確認ください</h3>
                                    <ul className="itami-sheet__list">
                                        {preBookingBullets.map((item) => (
                                            <li key={`notice-${item}`}>{item}</li>
                                        ))}
                                    </ul>
                                </section>
                            </div>

                            <div className="itami-sheet__actions">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    component={Link}
                                    to={selectedArea.ctaPath}
                                    onClick={closeSheet}
                                    className="itami-sheet__reserve"
                                    size="large"
                                    fullWidth
                                >
                                    {selectedArea.ctaLabel}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <p className="itami-body__lead">
                痛みを<strong>タップ</strong>！
            </p>
        </section>
    );
};

export default ItamiBody;

